import { ActionIcon, Popover, Stack, Text, Title } from '@mantine/core';
import { IconHelp } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { tokens } from '../ui/tokens.ts';

export interface HelpPopoverProps {
  /** Short label shown at the top of the popover. */
  title: string;
  /**
   * Rich body — accepts ReactNode so callers can embed lists, links, or
   * inline <code>. For plain text, pass a single paragraph string.
   */
  body: ReactNode;
  /** aria-label for the trigger icon button. */
  label?: string;
  /** Fixed popover width in px. Default 340. */
  width?: number;
}

/**
 * Compact contextual help trigger used in page headers and next to
 * confusing fields. Keeps explanations in-product rather than forcing
 * staff to open external docs — critical for onboarding new venues
 * (part-timers don't know "audit log" means domain events).
 *
 * Usage convention: ONE HelpPopover in each page's top-right corner
 * explaining what the page is for + optional field-level popovers
 * next to non-obvious inputs.
 */
export function HelpPopover({
  title,
  body,
  label = '這個頁面是做什麼的？',
  width = 340,
}: HelpPopoverProps): JSX.Element {
  return (
    <Popover width={width} position="bottom-end" shadow="md" withArrow>
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          color={tokens.color.mantine['primary']}
          aria-label={label}
          size="md"
        >
          <IconHelp size={18} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Title order={6}>{title}</Title>
          {typeof body === 'string' ? (
            <Text size="sm" c="dimmed">
              {body}
            </Text>
          ) : (
            body
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
